import { useState, useRef, useEffect, type FormEvent } from 'react';
import DatePicker from '../ui/DatePicker';
import { useTranslation } from 'react-i18next';
import { expensesApi, type ExpenseWrite, type ExtractedExpense } from '../../api/expenses.api';
import { fxApi } from '../../api/fx.api';
import { CurrencySelect } from '../ui/CurrencySelect';
import type { ExpenseCategory, Expense, PaymentMethod, ReservationLink, Trip } from '../../types';
import { Button } from '../ui/Button';
import ConfirmModal from '../ui/ConfirmModal';
import { toast } from '../ui/Toast';
import { useAuthStore } from '../../stores/auth.store';
import { SplitPicker } from './SplitPicker';
import { useSplitMembers } from '../../hooks/useSplitMembers';
import { defaultExpenseDate, EXPENSE_CATEGORIES } from './expenseDefaults';
import { withinLimit } from '../../utils/uploadLimits';
import { ReservationSelect } from './ReservationSelect';
import {
  expenseReservation, prefillFromReservation,
  type ReservationGroups, type ReservationOption,
} from '../../utils/reservations';

const round2 = (n: number) => Math.round(n * 100) / 100;

interface Props {
  tripId: string;
  trip: Trip;
  existing?: Expense;
  onSaved: (e: Expense) => void;
  onCancel: () => void;
  /** Only passed when editing: the ledger row has no delete button of its own. */
  onDelete?: () => Promise<void> | void;
  /**
   * A scan that found several expenses (a statement screenshot) is not a form
   * fill — it's a batch. The form hands the rows up and steps aside; without a
   * handler it keeps the first one, which is what it did before.
   */
  onMultipleScanned?: (expenses: ExtractedExpense[]) => void;
  /** A new expense opened from a ticket starts linked to it, and prefilled. */
  initialReservation?: ReservationOption;
  /** The trip's reservations, when the caller already has them. */
  reservations?: ReservationGroups;
}

export default function ExpenseForm({
  tripId, trip, existing, onSaved, onCancel, onDelete, onMultipleScanned, initialReservation, reservations,
}: Props) {
  const { t } = useTranslation();
  const { user, updateAiCredits, updatePremiumAiCredits } = useAuthStore();
  const [submitted, setSubmitted] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const receiptInputRef = useRef<HTMLInputElement>(null);
  const [scanning, setScanning] = useState(false);
  const err = (bad: boolean) => bad ? ' input-error' : '';

  const base = trip.budgetCurrency || 'EUR';

  const tripStart = trip.startDate.slice(0, 10);
  const defaultDate = defaultExpenseDate(trip);

  // When editing, the currency the user typed in is the original currency (if the
  // expense was converted) or the stored currency; amount is likewise the original.
  const initialCurrency = existing?.originalCurrency ?? existing?.currency ?? base;
  const initialAmount = existing != null
    ? String(existing.originalAmount ?? existing.amount)
    : '';

  const blankForm = {
    description: existing?.description ?? '',
    category: (existing?.category ?? 'FOOD') as ExpenseCategory,
    amount: initialAmount,
    currency: initialCurrency,
    paymentMethod: (existing?.paymentMethod ?? 'CARD') as PaymentMethod,
    status: (existing?.status ?? 'PAID') as 'PAID' | 'PENDING',
    date: existing?.date ? existing.date.slice(0, 10) : defaultDate,
    notes: existing?.notes ?? '',
  };
  // An existing expense's category and date are the user's; a new one's are
  // defaults a picked reservation may replace until the user changes them.
  const touched = useRef({ category: !!existing, date: !!existing });
  const [form, setForm] = useState(() =>
    initialReservation ? prefillFromReservation(blankForm, initialReservation, touched.current) : blankForm);
  const [reservation, setReservation] = useState<ReservationLink | null>(
    () => initialReservation?.link ?? (existing ? expenseReservation(existing) : null));

  function pickReservation(option: ReservationOption | null) {
    setReservation(option?.link ?? null);
    if (option) setForm(f => prefillFromReservation(f, option, touched.current));
  }
  const [loading, setLoading] = useState(false);

  // FX conversion state (only relevant when currency !== base)
  const [rate, setRate]             = useState(existing?.exchangeRate != null ? String(existing.exchangeRate) : '');
  const [rateDate, setRateDate]     = useState<string | null>(null);
  const [rateLoading, setRateLoading] = useState(false);
  const [rateError, setRateError]   = useState(false);
  // Currency we already have a rate for, so re-opening an edited expense keeps
  // its stored rate instead of silently re-fetching a fresh one.
  const fetchedFor = useRef<string | null>(existing?.originalCurrency ?? null);

  const needsConversion = form.currency !== base;

  async function fetchRate(force = false) {
    if (form.currency === base) return;
    if (!force && fetchedFor.current === form.currency && rate) return;
    setRateLoading(true);
    setRateError(false);
    try {
      const r = await fxApi.rate(form.currency, base);
      setRate(String(r.rate));
      setRateDate(r.date);
      fetchedFor.current = form.currency;
    } catch {
      setRateError(true);
    } finally {
      setRateLoading(false);
    }
  }

  useEffect(() => {
    if (form.currency === base) {
      setRateError(false);
      return;
    }
    fetchRate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.currency]);

  const amountNum = parseFloat(form.amount);
  const rateNum   = parseFloat(rate);
  const convertedBase = needsConversion
    ? (amountNum > 0 && rateNum > 0 ? round2(amountNum * rateNum) : null)
    : (amountNum > 0 ? amountNum : null);

  const allMembers = useSplitMembers(trip);

  // Who actually paid. Defaults to the current user, but any member can be
  // picked — one person often fronts the bill for the group and someone else
  // logs it.
  const [payerId, setPayerId] = useState(existing?.payerId ?? user?.id ?? '');

  // Pre-select members from existing splits, or all by default
  const [selected, setSelected] = useState<Set<string>>(
    existing?.splits.length
      ? new Set(existing.splits.map(s => s.userId))
      : new Set(allMembers.map(m => m.id)),
  );

  function toggleMember(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) { if (next.size > 1) next.delete(id); }
      else next.add(id);
      return next;
    });
  }

  const perPerson = selected.size > 0 && convertedBase != null
    ? (convertedBase / selected.size).toFixed(2)
    : null;

  function update(field: string, value: string) {
    if (field === 'category' || field === 'date') touched.current[field] = true;
    setForm(f => ({ ...f, [field]: value }));
  }

  async function handleScanReceipt(e: React.ChangeEvent<HTMLInputElement>) {
    // Up to three files: a long statement is usually screenshotted in pieces,
    // and the server reads them as one list (its own cap is 3 per request).
    const picked = Array.from(e.target.files ?? []).slice(0, 3);
    e.target.value = ''; // let the same file be re-picked after a failed scan
    if (picked.length === 0) return;
    const { accepted, errors } = withinLimit(picked, 'media');
    errors.forEach(message => toast(message, 'error'));
    if (accepted.length === 0) return;
    setScanning(true);
    try {
      const data = await expensesApi.extract(tripId, accepted);
      updateAiCredits(data.aiCreditsLeft);
      updatePremiumAiCredits(data.premiumAiCreditsLeft);
      if (data.expenses?.length > 1 && onMultipleScanned) {
        onMultipleScanned(data.expenses);
        return;
      }
      // Only overwrite fields the receipt actually yielded — leave the rest as-is.
      const first = data.expenses?.[0] ?? data;
      setForm(f => ({
        ...f,
        ...(first.description ? { description: first.description } : {}),
        ...(first.category ? { category: first.category } : {}),
        ...(first.amount != null ? { amount: String(first.amount) } : {}),
        ...(first.currency ? { currency: first.currency } : {}),
        ...(first.date ? { date: first.date } : {}),
        ...(first.paymentMethod ? { paymentMethod: first.paymentMethod } : {}),
        ...(first.status ? { status: first.status } : {}),
      }));
      toast(t('expenses.scan.success'), 'success');
    } catch (error) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      toast(status === 402 ? t('billing.aiPaywall.error') : t('expenses.scan.failed'), 'error');
    } finally {
      setScanning(false);
    }
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitted(true);
    if (!form.description.trim() || !form.amount || !form.date) return;
    if (needsConversion && !(rateNum > 0)) {
      toast(t('expenses.toast.rateRequired'), 'error');
      return;
    }
    setLoading(true);

    const total = convertedBase ?? 0;
    const participants = [...selected];
    const each = parseFloat((total / participants.length).toFixed(2));
    // Give any rounding remainder to the first person
    const remainder = parseFloat((total - each * participants.length).toFixed(2));
    const splitData = participants.map((userId, i) => ({
      userId,
      amount: i === 0 ? each + remainder : each,
    }));

    if (splitData.length === 0) {
      toast(t('expenses.toast.selectParticipant'), 'error');
      setLoading(false);
      return;
    }

    try {
      const payload = {
        description: form.description,
        category: form.category,
        payerId,
        amount: total,
        currency: base,
        originalAmount: needsConversion ? amountNum : null,
        originalCurrency: needsConversion ? form.currency : null,
        exchangeRate: needsConversion ? rateNum : null,
        paymentMethod: form.paymentMethod,
        status: form.status,
        date: form.date,
        notes: form.notes,
        splits: splitData,
        reservation,
      } as ExpenseWrite & { splits: { userId: string; amount: number }[] };
      const expense = existing
        ? await expensesApi.update(tripId, existing.id, payload)
        : await expensesApi.create(tripId, payload);
      toast(existing ? t('expenses.toast.updated') : t('expenses.toast.added'), 'success');
      onSaved(expense);
    } catch {
      toast(existing ? t('expenses.toast.failedUpdate') : t('expenses.toast.failedAdd'), 'error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <input
        ref={receiptInputRef}
        type="file"
        accept="image/*,application/pdf"
        multiple
        className="hidden"
        onChange={handleScanReceipt}
      />
      <button
        type="button"
        onClick={() => receiptInputRef.current?.click()}
        disabled={scanning}
        className="w-full flex items-center justify-center gap-2 rounded-xl border border-dashed border-teal-300 dark:border-teal-700 bg-teal-50/60 dark:bg-teal-900/10 px-3.5 py-3 text-sm font-semibold text-teal-700 dark:text-teal-300 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors disabled:opacity-60 disabled:pointer-events-none cursor-pointer"
      >
        {scanning ? (
          <>
            <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25"/><path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/></svg>
            {t('expenses.scan.scanning')}
          </>
        ) : (
          <>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9V6a2 2 0 0 1 2-2h3M21 9V6a2 2 0 0 0-2-2h-3M3 15v3a2 2 0 0 0 2 2h3M21 15v3a2 2 0 0 1-2 2h-3M3 12h18"/></svg>
            {t('expenses.scan.button')}
          </>
        )}
      </button>
      <ReservationSelect tripId={tripId} value={reservation} onChange={pickReservation} reservations={reservations} />
      <div>
        <label className="label">{t('expenses.form.description')} *</label>
        <input type="text" className={'input' + err(submitted && !form.description.trim())} value={form.description} onChange={e => update('description', e.target.value)} placeholder={t('expenses.form.descriptionPlaceholder')} />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">{t('expenses.form.category')}</label>
          <select className="input" value={form.category} onChange={e => update('category', e.target.value)}>
            {EXPENSE_CATEGORIES.map(c => <option key={c} value={c}>{t(`expenses.categories.${c}` as Parameters<typeof t>[0])}</option>)}
          </select>
        </div>
        <div>
          <label className="label">{t('expenses.form.payment')}</label>
          <select className="input" value={form.paymentMethod} onChange={e => update('paymentMethod', e.target.value)}>
            <option value="CARD">{t('expenses.payment.CARD')}</option>
            <option value="CASH">{t('expenses.payment.CASH')}</option>
            <option value="TRANSFER">{t('expenses.payment.TRANSFER')}</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className="label">{t('expenses.form.amount')} *</label>
          <input type="number" className={'input' + err(submitted && !form.amount)} step="0.01" min="0.01" value={form.amount} onChange={e => update('amount', e.target.value)} placeholder="0.00" />
        </div>
        <div>
          <label className="label">{t('expenses.form.currency')}</label>
          <CurrencySelect value={form.currency} onChange={v => update('currency', v)} />
        </div>
      </div>

      {/* Currency conversion — only when the expense currency differs from the trip's base */}
      {needsConversion && (
        <div className="rounded-xl border border-amber-200 dark:border-amber-800/60 bg-amber-50/60 dark:bg-amber-900/10 px-3.5 py-3 space-y-2.5">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-[11px] tracking-[0.08em] uppercase text-amber-700 dark:text-amber-400">
              {t('expenses.form.conversion.title', { currency: base })}
            </span>
            <button
              type="button"
              onClick={() => fetchRate(true)}
              disabled={rateLoading}
              className="text-[11px] font-semibold text-amber-700 dark:text-amber-400 hover:underline disabled:opacity-50 cursor-pointer"
            >
              {rateLoading ? t('expenses.form.conversion.loading') : t('expenses.form.conversion.refresh')}
            </button>
          </div>

          <div className="flex items-center gap-2 text-sm">
            <span className="font-mono text-slate-600 dark:text-slate-300 whitespace-nowrap">1 {form.currency} =</span>
            <input
              type="number"
              step="0.00000001"
              min="0"
              value={rate}
              onChange={e => setRate(e.target.value)}
              placeholder="0.00"
              className="input flex-1 py-1.5 font-mono"
            />
            <span className="font-mono text-slate-600 dark:text-slate-300">{base}</span>
          </div>

          {rateError && (
            <p className="text-[11px] text-amber-700 dark:text-amber-400">{t('expenses.form.conversion.manual')}</p>
          )}

          <div className="flex items-baseline justify-between gap-2 pt-0.5 border-t border-amber-200/70 dark:border-amber-800/40">
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {rateDate && !rateError ? t('expenses.form.conversion.asOf', { date: new Date(rateDate).toLocaleDateString() }) : ''}
            </span>
            <span className="font-mono text-sm font-bold text-slate-900 dark:text-slate-100">
              {convertedBase != null ? `${convertedBase.toFixed(2)} ${base}` : `— ${base}`}
            </span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">{t('expenses.form.date')}</label>
          <DatePicker value={form.date} onChange={v => update('date', v)} defaultMonth={tripStart} error={submitted && !form.date} />
        </div>
        <div>
          <label className="label">{t('expenses.form.status')}</label>
          <select className="input" value={form.status} onChange={e => update('status', e.target.value)}>
            <option value="PAID">{t('expenses.status.PAID')}</option>
            <option value="PENDING">{t('expenses.status.PENDING')}</option>
          </select>
        </div>
      </div>

      {/* Paid by — the person who fronted the money, not necessarily the one
          filling this in. Drives who is owed in the balances. */}
      <div>
        <label className="label" htmlFor="expense-payer">{t('expenses.form.paidBy')}</label>
        <select id="expense-payer" className="input" value={payerId} onChange={e => setPayerId(e.target.value)}>
          {allMembers.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
        </select>
      </div>

      <SplitPicker members={allMembers} selected={selected} onToggle={toggleMember} perPerson={perPerson} currency={base} />

      <div>
        <label className="label">{t('expenses.form.notes')}</label>
        <textarea className="input" rows={2} value={form.notes} onChange={e => update('notes', e.target.value)} />
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="submit" variant="cta" className="flex-1 disabled:opacity-50 disabled:pointer-events-none" loading={loading}>
          {existing ? t('expenses.form.saveChanges') : t('expenses.form.addExpense')}
        </Button>
        <button type="button" className="btn-secondary" onClick={onCancel}>{t('common.cancel')}</button>
      </div>

      {/* Delete link (editing only) */}
      {onDelete && (
        <button
          type="button"
          onClick={() => setConfirmDelete(true)}
          className="w-full text-xs text-red-400 hover:text-red-600 transition-colors cursor-pointer pt-1"
        >
          {t('expenses.delete.link')}
        </button>
      )}
      <ConfirmModal
        open={confirmDelete}
        title={t('expenses.delete.title')}
        body={t('common.confirmDelete')}
        loading={deleting}
        onConfirm={async () => {
          setDeleting(true);
          try {
            await onDelete?.();
            setConfirmDelete(false);
          } finally {
            setDeleting(false);
          }
        }}
        onCancel={() => setConfirmDelete(false)}
      />
    </form>
  );
}
